import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Unit, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const unitCreateSchema = z.object({
  name: z.string().min(1, '単位名は必須です'),
  priority: z.number().default(0),
});

export type UnitCreateInput = z.infer<typeof unitCreateSchema>;
export type UnitUpdateInput = Partial<UnitCreateInput>;

const COL = 'units';

// デフォルト固定単位。Firestoreには保存せず、取得時にクライアント側で先頭に合成する。
// 編集・削除は不可（固定値）。
const DEFAULT_UNIT_NAMES = ['式', '個', '台', '本', '回', '名', '時間', '人日', '人月'] as const;
const DEFAULT_UNIT_PREFIX = 'default-unit-';

export const DEFAULT_UNITS: Unit[] = DEFAULT_UNIT_NAMES.map((name, i) => ({
  id: `${DEFAULT_UNIT_PREFIX}${i}`,
  name,
  priority: i,
  isDefault: true,
  isDeleted: false,
  createdBy: 'system',
  updatedBy: 'system',
})) as unknown as Unit[];

export function isDefaultUnit(id: ID): boolean {
  return id.startsWith(DEFAULT_UNIT_PREFIX);
}

export async function fetchUnits(): Promise<Unit[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Unit);
  // 永続化されたデフォルト単位の並び順(priority)を反映する。
  // 未シードのデフォルトは正規の既定順にフォールバックする。
  const persisted = new Map(
    all.filter((u) => isDefaultUnit(u.id)).map((u) => [u.id, u.priority])
  );
  const defaults = DEFAULT_UNITS.map((u) => ({
    ...u,
    priority: persisted.get(u.id) ?? u.priority,
  })).sort((a, b) => a.priority - b.priority);
  // デフォルト固定単位と同名のカスタム単位は二重表示になるため除外する。
  // （過去の不正データ等でデフォルト名のカスタムドキュメントが混入しても重複しない）
  const defaultNames = new Set<string>(DEFAULT_UNIT_NAMES);
  const custom = all.filter(
    (u) => !isDefaultUnit(u.id) && !defaultNames.has(u.name)
  );
  // デフォルト固定単位を先頭に、ユーザー登録単位を後続に並べる
  return [...defaults, ...custom];
}

/**
 * デフォルト固定単位の表示順を永続化する。
 * Firestore上に未シードの場合は setDoc(merge) で作成しつつ priority を更新する。
 * 単位名・isDefaultは固定値として常に書き込む。管理者のみ許可。
 */
export async function reorderDefaultUnits(
  orderedIds: ID[],
  userId: ID
): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    const def = DEFAULT_UNITS.find((u) => u.id === id);
    if (!def) return;
    batch.set(
      doc(db, COL, id),
      { name: def.name, priority: index, isDefault: true, ...buildBaseFields(userId) },
      { merge: true }
    );
  });
  await batch.commit();
  await writeAuditLog({
    entityType: 'unit',
    entityId: 'default-order',
    action: 'update',
    userId,
    after: { order: orderedIds },
  });
}

export async function fetchUnit(id: ID): Promise<Unit | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Unit>(snap);
}

export async function createUnit(
  data: UnitCreateInput,
  userId: ID
): Promise<Unit> {
  const validated = parseOrThrow(unitCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as Unit;
  await writeAuditLog({
    entityType: 'unit',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateUnit(
  id: ID,
  data: UnitUpdateInput,
  userId: ID
): Promise<Unit> {
  if (isDefaultUnit(id)) throw new Error('デフォルト単位は編集できません。');
  const validated = unitCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('単位が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'unit',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as Unit;
}

export async function deleteUnit(id: ID, userId: ID): Promise<void> {
  if (isDefaultUnit(id)) throw new Error('デフォルト単位は削除できません。');
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('単位が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'unit',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
